import { useEffect, useState } from 'react';
import API from '../../api';
import { useToast } from '../../context/ToastContext';
import SearchSelect from '../../components/SearchSelect';

export default function EnseignantNotes() {
  const [evals, setEvals] = useState([]);
  const [selectedEval, setSelectedEval] = useState('');
  const [notes, setNotes] = useState([]);
  const [copies, setCopies] = useState([]);
  const [form, setForm] = useState({ evaluation: '', etudiant: '', valeur: '', commentaire: '' });
  const [editingId, setEditingId] = useState(null);
  const [etudiants, setEtudiants] = useState([]);
  const { addToast } = useToast();

  useEffect(() => {
    API.get('evaluations/').then((r) => setEvals(r.data.results || r.data));
  }, []);

  useEffect(() => {
    if (!selectedEval) return;
    API.get(`notes/?evaluation=${selectedEval}`).then((r) => setNotes(r.data.results || r.data));
    API.get(`copies/?evaluation=${selectedEval}`).then((r) => setCopies(r.data.results || r.data));
    const ev = evals.find((e) => e.id === selectedEval);
    if (ev && ev.cours) {
      API.get(`cours/${ev.cours}/participants/`).then((r) => {
        const data = r.data.results || r.data;
        setEtudiants(data.map((ins) => ({ id: ins.etudiant, first_name: ins.etudiant_nom.split(' ')[0] || '', last_name: ins.etudiant_nom.split(' ').slice(1).join(' ') || '', full_name: ins.etudiant_nom })));
      }).catch(() => {});
    }
  }, [selectedEval, evals]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await API.patch(`notes/${editingId}/`, { valeur: parseFloat(form.valeur), commentaire: form.commentaire });
        addToast('Note modifiée', 'success');
      } else {
        await API.post('notes/', { ...form, evaluation: selectedEval, valeur: parseFloat(form.valeur) });
        addToast('Note enregistrée', 'success');
      }
      setForm({ evaluation: '', etudiant: '', valeur: '', commentaire: '' });
      setEditingId(null);
      API.get(`notes/?evaluation=${selectedEval}`).then((r) => setNotes(r.data.results || r.data));
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.non_field_errors?.[0] || data?.detail || 'Erreur lors de l\'enregistrement';
      addToast(msg, 'error');
    }
  };

  const handleEdit = (n) => {
    setEditingId(n.id);
    setForm({ etudiant: n.etudiant, valeur: n.valeur, commentaire: n.commentaire || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ evaluation: '', etudiant: '', valeur: '', commentaire: '' });
  };

  return (
    <div>
      <div className="page-header">
        <h2><i className="bi bi-pencil-square" /> Gestion des notes</h2>
      </div>

      <SearchSelect
        className="select-filter-wrap"
        value={selectedEval}
        onChange={(e) => setSelectedEval(e.target.value)}
        options={evals.map((ev) => ({ value: ev.id, label: `${ev.titre} (${ev.cours_titre})` }))}
        placeholder="-- Sélectionner une évaluation --"
      />

      {selectedEval && (
        <>
          <div className="section">
            <h3>
              <i className="bi bi-file-earmark-text" /> Copies soumises
              <span style={{ marginLeft: 8, fontSize: 14, color: 'var(--text-secondary)' }}>
                ({copies.length} / {etudiants.length} étudiants)
              </span>
            </h3>
            {copies.length === 0 ? (
              <div className="empty-state"><i className="bi bi-inbox" /><p>Aucune copie soumise</p></div>
            ) : (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead><tr><th>Étudiant</th><th>Soumise le</th><th>Statut</th><th>Fichier</th></tr></thead>
                  <tbody>
                    {copies.map((c) => {
                      const hasNote = notes.some((n) => String(n.etudiant) === String(c.etudiant));
                      return (
                        <tr key={c.id}>
                          <td>{c.etudiant_nom}</td>
                          <td>{new Date(c.soumis_le).toLocaleString('fr-FR')}</td>
                          <td>{hasNote
                            ? <span className="badge-status badge-published"><i className="bi bi-check-circle" /> Noté</span>
                            : <span className="badge-status badge-draft">En attente</span>}
                          </td>
                          <td><a href={c.fichier_url || c.fichier} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary"><i className="bi bi-download" /> Télécharger</a></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="section">
            <h3><i className={editingId ? "bi bi-pencil" : "bi bi-plus-circle"} /> {editingId ? 'Modifier la note' : 'Attribuer une note'}</h3>
            <div className="form-glass">
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Étudiant</label>
                    {editingId ? (
                      <input className="form-control" value={etudiants.find(s => String(s.id) === String(form.etudiant))?.full_name || ''} disabled />
                    ) : (
                    <SearchSelect
                      name="etudiant"
                      value={form.etudiant}
                      onChange={handleChange}
                      options={etudiants.map((s) => ({ value: s.id, label: s.full_name || `${s.first_name} ${s.last_name}` }))}
                      placeholder="-- Sélectionner --"
                      required
                    />
                    )}
                  </div>
                  <div className="form-group">
                    <label>Note</label>
                    <input className="form-control" name="valeur" type="number" step="0.01" placeholder="Note" value={form.valeur} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Commentaire</label>
                    <input className="form-control" name="commentaire" placeholder="Commentaire (optionnel)" value={form.commentaire} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary"><i className={editingId ? "bi bi-check-lg" : "bi bi-check-lg"} /> {editingId ? 'Modifier' : 'Enregistrer'}</button>
                  {editingId && <button type="button" className="btn btn-secondary" onClick={cancelEdit}><i className="bi bi-x-lg" /> Annuler</button>}
                </div>
              </form>
            </div>
          </div>

          <div className="section">
            <h3><i className="bi bi-list-check" /> Notes enregistrées</h3>
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead><tr><th>Étudiant</th><th>Note</th><th>Commentaire</th><th>Actions</th></tr></thead>
                <tbody>
                  {notes.length === 0 ? (
                    <tr><td colSpan="4"><div className="empty-state"><i className="bi bi-pencil-square" /><p>Aucune note</p></div></td></tr>
                  ) : notes.map((n) => {
                    const pct = n.bareme ? (n.valeur / n.bareme) * 20 : 0;
                    const cls = pct >= 10 ? 'note-success' : pct >= 8 ? 'note-warning' : 'note-danger';
                    return (
                    <tr key={n.id}>
                      <td>{n.etudiant_nom}</td>
                      <td><strong className={`moyenne ${cls}`}>{parseFloat(n.valeur).toFixed(2)}/{n.bareme}</strong></td>
                      <td>{n.commentaire}</td>
                      <td><button className="btn btn-sm btn-secondary" onClick={() => handleEdit(n)}><i className="bi bi-pencil" /> Modifier</button></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
